import React, { useState, useEffect } from 'react';
import './PortfolioViewer.css';

interface PortfolioEntry {
  id: string;
  sessionId: string;
  entryType: 'snapshot' | 'cop_product' | 'milestone';
  date: Date;
  sceneId: string;
  layerId: string;
  sceneTitle?: string;
  disciplineType?: 'music' | 'dance' | 'drama';
  metrics: any;
  teacherFeedback?: any;
  studentReflection?: string;
  rubricScores?: any[];
  recordingUrl?: string;
}

interface PortfolioFilters {
  startDate?: string;
  endDate?: string;
  sceneId?: string;
  layerId?: string;
  disciplineType?: 'music' | 'dance' | 'drama';
  entryType?: 'snapshot' | 'cop_product' | 'milestone';
}

interface GrowthMetric {
  metricName: string;
  dataPoints: { date: Date; value: number }[];
  trend: 'improving' | 'declining' | 'stable';
  percentChange: number;
}

interface PortfolioViewerProps {
  userId: string;
  onEntrySelect?: (entryId: string) => void;
}

/**
 * PortfolioViewer displays student portfolio entries with filtering and growth tracking
 * Requirements: 14.2, 14.5
 */
export const PortfolioViewer: React.FC<PortfolioViewerProps> = ({ userId, onEntrySelect }) => {
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const [entries, setEntries] = useState<PortfolioEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<PortfolioEntry[]>([]);
  const [growthMetrics, setGrowthMetrics] = useState<GrowthMetric[]>([]);
  const [filters, setFilters] = useState<PortfolioFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'entries' | 'growth'>('entries');
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);
  const [reflectionText, setReflectionText] = useState('');
  const [editingReflection, setEditingReflection] = useState<string | null>(null);

  // Fetch portfolio entries
  useEffect(() => {
    fetchPortfolio();
  }, [userId]);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [entries, filters]);

  // Fetch growth metrics when switching to growth view
  useEffect(() => {
    if (viewMode === 'growth') {
      fetchGrowthMetrics();
    }
  }, [viewMode, filters.sceneId, filters.layerId]);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.sceneId) queryParams.append('sceneId', filters.sceneId);
      if (filters.layerId) queryParams.append('layerId', filters.layerId);
      if (filters.disciplineType) queryParams.append('disciplineType', filters.disciplineType);
      if (filters.entryType) queryParams.append('entryType', filters.entryType);

      const response = await fetch(`${apiBase}/portfolio/${userId}?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch portfolio');
      
      const data = await response.json();
      setEntries(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrowthMetrics = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.sceneId) queryParams.append('sceneId', filters.sceneId);
      if (filters.layerId) queryParams.append('layerId', filters.layerId);

      const response = await fetch(`${apiBase}/portfolio/${userId}/growth?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch growth metrics');
      
      const data = await response.json();
      setGrowthMetrics(data);
    } catch (err) {
      console.error('Error fetching growth metrics:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...entries];

    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(entry => new Date(entry.date) >= startDate);
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      filtered = filtered.filter(entry => new Date(entry.date) <= endDate);
    }

    if (filters.sceneId) {
      filtered = filtered.filter(entry => entry.sceneId === filters.sceneId);
    }

    if (filters.layerId) {
      filtered = filtered.filter(entry => entry.layerId === filters.layerId);
    }

    if (filters.disciplineType) {
      filtered = filtered.filter(entry => entry.disciplineType === filters.disciplineType);
    }

    if (filters.entryType) {
      filtered = filtered.filter(entry => entry.entryType === filters.entryType);
    }

    setFilteredEntries(filtered);
  };

  const handleFilterChange = (key: keyof PortfolioFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const handleAddReflection = async (entryId: string) => {
    if (!reflectionText.trim()) return;

    try {
      const response = await fetch(`${apiBase}/portfolio/entries/${entryId}/reflection`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reflection: reflectionText })
      });

      if (!response.ok) throw new Error('Failed to add reflection');

      // Update local state
      setEntries(prev => prev.map(entry => 
        entry.id === entryId 
          ? { ...entry, studentReflection: reflectionText }
          : entry
      ));

      setReflectionText('');
      setEditingReflection(null);
    } catch (err) {
      console.error('Error adding reflection:', err);
    }
  };

  const getEntryTypeLabel = (type: string) => {
    switch (type) {
      case 'snapshot': return 'Practice Snapshot';
      case 'cop_product': return 'Teacher Assessment';
      case 'milestone': return 'Milestone';
      default: return type;
    }
  };

  const getDisciplineIcon = (discipline?: string) => {
    switch (discipline) {
      case 'music': return '🎵';
      case 'dance': return '💃';
      case 'drama': return '🎭';
      default: return '📝';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      case 'stable': return '➡️';
      default: return '';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const extractKeyMetrics = (metrics: any): string[] => {
    const keyMetrics: string[] = [];
    
    if (metrics?.technical?.pitchAccuracy) {
      const avgAccuracy = metrics.technical.pitchAccuracy.reduce((sum: number, m: any) => 
        sum + (100 - Math.abs(m.deviation)), 0) / metrics.technical.pitchAccuracy.length;
      keyMetrics.push(`Pitch: ${avgAccuracy.toFixed(1)}%`);
    }

    if (metrics?.technical?.rhythmAccuracy) {
      const avgAccuracy = metrics.technical.rhythmAccuracy.reduce((sum: number, m: any) => 
        sum + (100 - Math.abs(m.deviation / 10)), 0) / metrics.technical.rhythmAccuracy.length;
      keyMetrics.push(`Rhythm: ${avgAccuracy.toFixed(1)}%`);
    }

    if (metrics?.technical?.beatSyncScore !== undefined) {
      keyMetrics.push(`Beat Sync: ${metrics.technical.beatSyncScore}%`);
    }

    if (metrics?.social?.groupSyncScore !== undefined) {
      keyMetrics.push(`Group Sync: ${metrics.social.groupSyncScore}%`);
    }

    return keyMetrics.slice(0, 3); // Show top 3 metrics
  };

  if (loading) {
    return <div className="portfolio-viewer loading">Loading portfolio...</div>;
  }

  if (error) {
    return <div className="portfolio-viewer error">Error: {error}</div>;
  }

  return (
    <div className="portfolio-viewer">
      <div className="portfolio-header">
        <h2>My Portfolio</h2>
        <div className="view-mode-toggle">
          <button
            className={viewMode === 'entries' ? 'active' : ''}
            onClick={() => setViewMode('entries')}
          >
            Entries
          </button>
          <button
            className={viewMode === 'growth' ? 'active' : ''}
            onClick={() => setViewMode('growth')}
          >
            Growth Trends
          </button>
        </div>
      </div>

      <div className="portfolio-filters">
        <input
          type="date"
          placeholder="Start Date"
          value={filters.startDate || ''}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
        />
        <input
          type="date"
          placeholder="End Date"
          value={filters.endDate || ''}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
        />
        <select
          value={filters.disciplineType || ''}
          onChange={(e) => handleFilterChange('disciplineType', e.target.value)}
        >
          <option value="">All Disciplines</option>
          <option value="music">Music</option>
          <option value="dance">Dance</option>
          <option value="drama">Drama</option>
        </select>
        <select
          value={filters.entryType || ''}
          onChange={(e) => handleFilterChange('entryType', e.target.value)}
        >
          <option value="">All Types</option>
          <option value="snapshot">Practice Snapshots</option>
          <option value="cop_product">Teacher Assessments</option>
          <option value="milestone">Milestones</option>
        </select>
        <button onClick={() => setFilters({})}>Clear Filters</button>
      </div>

      {viewMode === 'entries' ? (
        <div className="portfolio-entries">
          {filteredEntries.length === 0 ? (
            <div className="no-entries">No portfolio entries found</div>
          ) : (
            filteredEntries.map(entry => (
              <div
                key={entry.id}
                className={`portfolio-entry ${selectedEntry === entry.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedEntry(entry.id);
                  onEntrySelect?.(entry.id);
                }}
              >
                <div className="entry-header">
                  <div className="entry-icon">{getDisciplineIcon(entry.disciplineType)}</div>
                  <div className="entry-info">
                    <h3>{entry.sceneTitle || 'Untitled Scene'}</h3>
                    <span className="entry-type">{getEntryTypeLabel(entry.entryType)}</span>
                    <span className="entry-date">{formatDate(entry.date)}</span>
                  </div>
                </div>

                <div className="entry-metrics">
                  {extractKeyMetrics(entry.metrics).map((metric, idx) => (
                    <span key={idx} className="metric-badge">{metric}</span>
                  ))}
                </div>

                {entry.teacherFeedback && (
                  <div className="entry-feedback">
                    <strong>Teacher Feedback:</strong>
                    <p>{entry.teacherFeedback.overallComment || 'Feedback provided'}</p>
                  </div>
                )}

                {entry.rubricScores && entry.rubricScores.length > 0 && (
                  <div className="entry-rubric">
                    <strong>Rubric Score:</strong> {entry.rubricScores.length} criteria assessed
                  </div>
                )}

                {entry.studentReflection ? (
                  <div className="entry-reflection">
                    <strong>My Reflection:</strong>
                    <p>{entry.studentReflection}</p>
                  </div>
                ) : (
                  editingReflection === entry.id ? (
                    <div className="entry-reflection-edit">
                      <textarea
                        value={reflectionText}
                        onChange={(e) => setReflectionText(e.target.value)}
                        placeholder="Add your reflection..."
                        rows={3}
                      />
                      <div className="reflection-actions">
                        <button onClick={() => handleAddReflection(entry.id)}>Save</button>
                        <button onClick={() => {
                          setEditingReflection(null);
                          setReflectionText('');
                        }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="add-reflection-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingReflection(entry.id);
                      }}
                    >
                      Add Reflection
                    </button>
                  )
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="portfolio-growth">
          {growthMetrics.length === 0 ? (
            <div className="no-growth-data">
              Not enough data to show growth trends. Complete more practice sessions!
            </div>
          ) : (
            <div className="growth-metrics">
              {growthMetrics.map((metric, idx) => (
                <div key={idx} className="growth-metric-card">
                  <div className="metric-header">
                    <h3>{metric.metricName}</h3>
                    <span className="trend-indicator">
                      {getTrendIcon(metric.trend)} {metric.trend}
                    </span>
                  </div>
                  <div className="metric-change">
                    <span className={`change-value ${metric.percentChange > 0 ? 'positive' : 'negative'}`}>
                      {metric.percentChange > 0 ? '+' : ''}{metric.percentChange}%
                    </span>
                  </div>
                  <div className="metric-chart">
                    {/* Simple line chart visualization */}
                    <svg width="100%" height="100" viewBox="0 0 300 100">
                      <polyline
                        points={metric.dataPoints.map((dp, i) => {
                          const x = (i / (metric.dataPoints.length - 1)) * 280 + 10;
                          const y = 90 - (dp.value / Math.max(...metric.dataPoints.map(d => d.value))) * 70;
                          return `${x},${y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="#4CAF50"
                        strokeWidth="2"
                      />
                      {metric.dataPoints.map((dp, i) => {
                        const x = (i / (metric.dataPoints.length - 1)) * 280 + 10;
                        const y = 90 - (dp.value / Math.max(...metric.dataPoints.map(d => d.value))) * 70;
                        return (
                          <circle
                            key={i}
                            cx={x}
                            cy={y}
                            r="3"
                            fill="#4CAF50"
                          />
                        );
                      })}
                    </svg>
                  </div>
                  <div className="metric-data-points">
                    {metric.dataPoints.length} practice sessions
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
